import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Button, Input } from '../../components/ui';
import { usePreferences } from '../../contexts/PreferencesContext';
import { api } from '../../services';
import logoClaro from '../../assets/Logo_claro.png';
import logoEscuro from '../../assets/Logo_escuro.png';
import styles from './ResetPassword.module.css';

export function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const navigate = useNavigate();
    const { t, prefs } = usePreferences();
    const darkMode = prefs.darkMode;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');

        if (!newPassword || !confirmPassword) {
            setError(t('resetPassword.erroCampos'));
            return;
        }

        if (newPassword !== confirmPassword) {
            setError(t('resetPassword.erroSenhasDiferentes'));
            return;
        }

        if (newPassword.length < 6) {
            setError(t('resetPassword.erroSenhaFraca'));
            return;
        }

        if (!token) {
            setError(t('resetPassword.erroTokenInvalido'));
            return;
        }

        setIsLoading(true);
        try {
            await api.resetPassword(token, newPassword);
            setSuccess(true);
        } catch {
            setError(t('resetPassword.erroTokenExpirado'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.logo}>
                    <img
                        src={darkMode ? logoClaro : logoEscuro}
                        alt="Financeiro Diário"
                        className={styles.logoImg}
                    />
                </div>

                <div className={styles.header}>
                    <h1 className={styles.title}>{t('resetPassword.titulo')}</h1>
                    <p className={styles.subtitle}>{t('resetPassword.subtitulo')}</p>
                </div>

                {success ? (
                    <div>
                        <div className={styles.successMessage}>
                            {t('resetPassword.sucesso')}
                        </div>
                        <button
                            type="button"
                            className={styles.backLink}
                            onClick={() => navigate('/login')}
                        >
                            {t('login.voltarLogin')}
                        </button>
                    </div>
                ) : (
                    <form className={styles.form} onSubmit={handleSubmit}>
                        {error && <div className={styles.error}>{error}</div>}

                        <Input
                            type={showPassword ? 'text' : 'password'}
                            label={t('resetPassword.novaSenha')}
                            placeholder="••••••••"
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            rightElement={
                                <button
                                    type="button"
                                    className={styles.toggleVisibility}
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label={showPassword ? t('register.ocultarSenha') : t('register.mostrarSenha')}
                                >
                                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                </button>
                            }
                            fullWidth
                        />

                        <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            label={t('resetPassword.confirmarSenha')}
                            placeholder="••••••••"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            rightElement={
                                <button
                                    type="button"
                                    className={styles.toggleVisibility}
                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    aria-label={
                                        showConfirmPassword ? t('register.ocultarSenha') : t('register.mostrarSenha')
                                    }
                                >
                                    {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                </button>
                            }
                            fullWidth
                        />

                        <Button type="submit" fullWidth isLoading={isLoading}>
                            {t('resetPassword.salvar')}
                        </Button>

                        <p className={styles.footer}>
                            <button
                                type="button"
                                className={styles.footerLink}
                                onClick={() => navigate('/login')}
                            >
                                {t('login.voltarLogin')}
                            </button>
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}
